import { BaseWidget, WidgetConstructor } from './model';

/**
 * Abstract base class for widget factories.
 * Provides shared implementation for registry management methods.
 * Framework-specific factories should extend this class.
 */
export abstract class BaseWidgetFactory<
  TConstructor extends WidgetConstructor,
  TParams extends ConstructorParameters<TConstructor> = ConstructorParameters<TConstructor>,
> {
  protected readonly registry = new Map<string, TConstructor>();

  // Register a widget type with the factory.
  register(type: string, widgetClass: TConstructor): void {
    if (this.registry.has(type)) {
      console.warn(`Widget type '${type}' is already registered. Overwriting.`);
    }
    this.registry.set(type, widgetClass);
  }

  /**
   * Create a widget instance of the specified type.
   * Handles registry lookup and error handling; delegates instantiation to subclasses.
   */
  create(type: string, container: HTMLElement, ...args: TParams): BaseWidget {
    const WidgetClass = this.registry.get(type);

    if (!WidgetClass) {
      throw new Error(
        `Unknown widget type: '${type}'. Available types: ${Array.from(this.registry.keys()).join(', ')}`,
      );
    }

    try {
      return this.instantiate(WidgetClass, container, ...args);
    } catch (error) {
      throw new Error(`Failed to create widget of type '${type}': ${String(error)}`);
    }
  }

  // Get the metadata for a widget type.
  getMetadata(type: string): TConstructor['metadata'] | null {
    const WidgetClass = this.registry.get(type);
    return WidgetClass?.metadata ?? null;
  }

  // List all available widget types with their metadata.
  listAvailableWidgets(): Array<NonNullable<TConstructor['metadata']>> {
    const metadataList: Array<NonNullable<TConstructor['metadata']>> = [];

    this.registry.forEach((WidgetClass, type) => {
      if (WidgetClass.metadata) {
        metadataList.push(WidgetClass.metadata);
      } else {
        // Create minimal metadata if not provided
        const minimalMetadata: NonNullable<TConstructor['metadata']> = {
          type,
          description: `Widget of type ${type}`,
          parameters: [],
          displayFeatures: [],
          supportedActions: [],
          focusableRegions: [],
          highlightableObjects: [],
        };
        metadataList.push(minimalMetadata);
      }
    });

    return metadataList;
  }

  // Check if a widget type is registered.
  has(type: string): boolean {
    return this.registry.has(type);
  }

  // Get all registered widget type names.
  getRegisteredTypes(): string[] {
    return Array.from(this.registry.keys());
  }

  // Framework-specific: instantiate the widget with framework-specific dependencies
  protected abstract instantiate(WidgetClass: TConstructor, container: HTMLElement, ...args: TParams): BaseWidget;

  // Framework-specific: load the framework library
  abstract loadFramework(): Promise<unknown>;
}
